(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var CART_ENDPOINTS = {
    add: '/cart/add.js',
    change: '/cart/change.js',
    get: '/cart.js'
  };

  var drawer = null;
  var panel = null;
  var overlay = null;
  var abortController = null;
  var isOpen = false;
  var lastFocusedElement = null;
  var lastCart = null;
  var initialized = false;

  // ---------- DOM utilities ----------

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  // ---------- Section Rendering API helpers ----------

  // The drawer lives in the `header` section group; its element id equals the
  // group's section key (e.g. "oddit_cart_drawer"), which is the id the Section
  // Rendering API expects.
  function getSectionId() {
    return (drawer && (drawer.getAttribute('data-section-id') || drawer.id)) || '';
  }

  function sectionsBody(body) {
    var id = getSectionId();
    if (id) {
      body.sections = id;
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  function sectionHTMLFrom(response) {
    var id = getSectionId();
    if (!id || !response || !response.sections) return null;
    return response.sections[id] || null;
  }

  // Fetch the freshly-rendered drawer section straight from Shopify (used on
  // open and manual refresh, since /cart.js can't return rendered sections).
  function fetchSectionHTML() {
    var id = getSectionId();
    if (!id) return Promise.resolve(null);
    var url = window.location.pathname + '?sections=' + encodeURIComponent(id);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        return data ? data[id] : null;
      })
      .catch(function () {
        return null;
      });
  }

  // ---------- Cart AJAX API ----------

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
          err.status = response.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(CART_ENDPOINTS.get, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
      })
      .then(function (cart) {
        lastCart = cart;
        return cart;
      });
  }

  function addToCart(variantId, quantity, properties) {
    var qty = quantity == null ? 1 : quantity;
    var body = {
      id: Number(variantId),
      quantity: qty
    };
    if (properties && typeof properties === 'object') {
      body.properties = properties;
    }
    return postJSON(CART_ENDPOINTS.add, body).then(function () {
      // add.js returns the added line(s), not the whole cart — fetch the full
      // cart (for lastCart + the badge) and the freshly-rendered drawer.
      return Promise.all([getCart(), fetchSectionHTML()]).then(function (results) {
        applyCart(results[0], results[1]);
        return results[0];
      });
    });
  }

  function addMultipleToCart(items) {
    var payload = {
      items: (items || []).map(function (item) {
        var entry = { id: Number(item.id), quantity: item.quantity == null ? 1 : item.quantity };
        if (item.properties && typeof item.properties === 'object') {
          entry.properties = item.properties;
        }
        if (item.selling_plan) {
          entry.selling_plan = item.selling_plan;
        }
        return entry;
      })
    };
    return postJSON(CART_ENDPOINTS.add, payload).then(function () {
      return Promise.all([getCart(), fetchSectionHTML()]).then(function (results) {
        applyCart(results[0], results[1]);
        return results[0];
      });
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return postJSON(CART_ENDPOINTS.change, sectionsBody({
      id: lineItemKey,
      quantity: Number(quantity)
    })).then(function (cart) {
      applyCart(cart, sectionHTMLFrom(cart));
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

function updateLineItemToSubscription(lineItemKey, sellingPlanId, quantity) {
    // selling_plan must be a NUMBER (change.js ignores a string id, so the
    // upgrade silently did nothing); and no section params (same store quirk as
    // add.js) — render the drawer from a separate section GET instead.
    // quantity MUST be sent — change.js resets the line to 1 when it's omitted.
    var body = {
      id: lineItemKey,
      selling_plan: Number(sellingPlanId)
    };
    var qty = Number(quantity);
    if (qty > 0) body.quantity = qty;
    return postJSON(CART_ENDPOINTS.change, body).then(function (cart) {
      return fetchSectionHTML().then(function (html) {
        applyCart(cart, html);
        return cart;
      });
    });
  }

  function refreshCart() {
    return Promise.all([getCart(), fetchSectionHTML()]).then(function (results) {
      applyCart(results[0], results[1]);
      return results[0];
    });
  }

  // ---------- Rendering ----------

  // Swap the freshly-rendered section markup into the live drawer. We only
  // replace the inner content so the outer drawer, overlay, and panel (which
  // carry the open/close animation state) stay put.
  function renderDrawerHTML(html) {
    if (!drawer || !html) return;
    var temp = document.createElement('div');
    temp.innerHTML = html;
    var newInner = temp.querySelector('.oddit-cart-drawer__inner');
    var liveInner = drawer.querySelector('.oddit-cart-drawer__inner');
    if (!newInner || !liveInner) return;
    liveInner.innerHTML = newInner.innerHTML;
    // The carousel handlers were bound to elements that just got replaced.
    bindUpsellCarousel(abortController ? abortController.signal : undefined);
  }

  // Single source of truth for "the cart changed": update state, optionally
  // swap in server-rendered HTML, and notify the rest of the theme.
  function applyCart(cart, html) {
    if (cart) lastCart = cart;
    if (html) renderDrawerHTML(html);
    if (cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    }
  }

  // ---------- UI: drawer open/close, focus trap, body lock ----------

  function lockBodyScroll() {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  function getFocusableElements() {
    if (!panel) return [];
    var selectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    return $all(selectors.join(','), panel).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !isOpen) return;
    var focusables = getFocusableElements();
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first || !panel.contains(document.activeElement)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function openDrawer() {
    if (!drawer || isOpen) return;
    lastFocusedElement = document.activeElement;
    drawer.style.display = '';
    drawer.classList.remove('is-closing');
    // Trigger reflow so the panel's closed transform is committed before we
    // flip to the open state — otherwise the slide transition won't run.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    isOpen = true;
    lockBodyScroll();
    drawer.setAttribute('aria-hidden', 'false');

    // Focus first focusable element
    setTimeout(function () {
      var focusables = getFocusableElements();
      if (focusables.length) focusables[0].focus();
    }, 0);

    document.dispatchEvent(new CustomEvent('cart-drawer:opened'));

    // Refresh cart on open
    refreshCart().catch(function () {});
  }

  function closeDrawer() {
    if (!drawer || !isOpen) return;
    drawer.classList.remove('is-open');
    drawer.classList.add('is-closing');
    isOpen = false;
    unlockBodyScroll();
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      try { lastFocusedElement.focus(); } catch (e) {}
    }
    setTimeout(function () {
      if (!isOpen) {
        drawer.style.display = 'none';
        drawer.classList.remove('is-closing');
      }
    }, 350);

    document.dispatchEvent(new CustomEvent('cart-drawer:closed'));
  }

  function toggleDrawer() {
    if (isOpen) closeDrawer();
    else openDrawer();
  }

  function handleKeydown(event) {
    if (!isOpen) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeDrawer();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  }

  // ---------- Event binding ----------

  function bindEvents() {
    if (!drawer) return;
    if (abortController) {
      try { abortController.abort(); } catch (e) {}
    }
    abortController = new AbortController();
    var signal = abortController.signal;

    // Keyboard
    document.addEventListener('keydown', handleKeydown, { signal: signal });

    // Delegated clicks. Bound to the drawer so handlers survive section-HTML
    // swaps (close button, line items, and upsell cards all get re-rendered).
    drawer.addEventListener('click', function (event) {
      var target = event.target.closest('[data-cart-drawer-close], [data-quantity-decrease], [data-quantity-increase], [data-quantity-action], [data-remove-item], [data-remove-line], [data-subscribe-upsell], [data-add-to-cart], [data-remove-free-gift], [data-cart-checkout]');
      if (!target || !drawer.contains(target)) return;

      // Close (X button and overlay both carry data-cart-drawer-close)
      if (target.matches('[data-cart-drawer-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      // Quantity decrease
      if (target.matches('[data-quantity-decrease]') || target.getAttribute('data-quantity-action') === 'decrease') {
        event.preventDefault();
        var keyD = target.getAttribute('data-line-item-key') || target.getAttribute('data-line-key');
        if (!keyD) return;
        var item = findLineItem(keyD);
        var currentQtyD = item ? Number(item.quantity) : 1;
        var newQtyD = Math.max(0, currentQtyD - 1);
        target.disabled = true;
        updateLineItemQuantity(keyD, newQtyD)
          .catch(function () {})
          .then(function () { target.disabled = false; });
        return;
      }

      // Quantity increase
      if (target.matches('[data-quantity-increase]') || target.getAttribute('data-quantity-action') === 'increase') {
        event.preventDefault();
        var keyI = target.getAttribute('data-line-item-key') || target.getAttribute('data-line-key');
        if (!keyI) return;
        var itemI = findLineItem(keyI);
        var currentQtyI = itemI ? Number(itemI.quantity) : 1;
        target.disabled = true;
        updateLineItemQuantity(keyI, currentQtyI + 1)
          .catch(function () {})
          .then(function () { target.disabled = false; });
        return;
      }

      // Remove line item
      if (target.matches('[data-remove-item]') || target.matches('[data-remove-line]')) {
        event.preventDefault();
        var keyR = target.getAttribute('data-line-item-key') || target.getAttribute('data-line-key');
        if (!keyR) return;
        target.disabled = true;
        removeFromCart(keyR)
          .catch(function () {})
          .then(function () { target.disabled = false; });
        return;
      }

// Subscribe upsell
      if (target.matches('[data-subscribe-upsell]')) {
        event.preventDefault();
        var keyS = target.getAttribute('data-line-item-key') || target.getAttribute('data-line-key');
        var planId = target.getAttribute('data-selling-plan-id');
        var qtyS = target.getAttribute('data-line-quantity');
        if (!keyS || !planId) return;
        target.disabled = true;
        target.classList.add('is-loading');
        target.setAttribute('aria-busy', 'true');
        updateLineItemToSubscription(keyS, planId, qtyS)
          .catch(function () {})
          .then(function () {
            // On success the drawer re-renders and replaces this button, so this
            // runs on a detached node (harmless); on failure it restores the button.
            target.disabled = false;
            target.classList.remove('is-loading');
            target.removeAttribute('aria-busy');
          });
        return;
      }

      // Cross-sell / upsell add
      if (target.matches('[data-add-to-cart]') || target.matches('.oddit-cart-drawer__upsell-add') || target.matches('.oddit-cart-drawer__rec-add') || target.matches('.oddit-cart-drawer__upsell-cta')) {
        event.preventDefault();
        var variantId = target.getAttribute('data-variant-id');
        if (!variantId) return;
        var planAttr = target.getAttribute('data-selling-plan-id');
        target.disabled = true;
        var labelEl = target.querySelector('[data-button-label]');
        var originalLabel = labelEl ? labelEl.textContent : null;
        if (labelEl) labelEl.textContent = 'Adding…';

        var addPromise;
        if (planAttr) {
          addPromise = addMultipleToCart([{ id: Number(variantId), quantity: 1, selling_plan: planAttr }]);
        } else {
          addPromise = addToCart(Number(variantId), 1);
        }
        addPromise
          .catch(function () {})
          .then(function () {
            target.disabled = false;
            if (labelEl && originalLabel != null) labelEl.textContent = originalLabel;
          });
        return;
      }

      // Remove free gift (treat as remove of matching line item if present)
      if (target.matches('[data-remove-free-gift]')) {
        event.preventDefault();
        var giftEl = target.closest('[data-free-gift]');
        var giftKey = giftEl ? giftEl.getAttribute('data-line-item-key') : null;
        if (giftKey) {
          removeFromCart(giftKey).catch(function () {});
        }
        return;
      }

      // Checkout button — show a loading spinner while the browser navigates to
      // checkout (the request can take a moment). Anchors navigate naturally;
      // non-anchor buttons are sent to checkout manually.
      if (target.matches('[data-cart-checkout]')) {
        if (target.classList.contains('is-loading')) {
          // Already navigating — swallow repeat clicks.
          event.preventDefault();
          return;
        }
        target.classList.add('is-loading');
        target.setAttribute('aria-busy', 'true');
        if (target.tagName === 'BUTTON') {
          event.preventDefault();
          window.location.href = '/checkout';
        }
        return;
      }
    }, { signal: signal });

    // Checkout CTA button (class-based fallback)
    $all('.oddit-cart-drawer__checkout-cta', drawer).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('is-loading')) return;
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        window.location.href = '/checkout';
      }, { signal: signal });
    });

    // If the shopper returns via the back/forward cache, clear any checkout
    // spinner left over from the previous navigation.
    window.addEventListener('pageshow', function () {
      $all('[data-cart-checkout].is-loading, .oddit-cart-drawer__checkout-cta.is-loading', drawer).forEach(function (btn) {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      });
    }, { signal: signal });

    // Upsell carousel dots & track interactions
    bindUpsellCarousel(signal);

    // Listen for external add-to-cart calls / cart updates
    document.addEventListener('cart:request-open', function () {
      openDrawer();
    }, { signal: signal });

    // Theme editor compatibility
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && event.target.contains(drawer)) {
        unbindAndInit();
      }
    }, { signal: signal });

    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target && event.target.contains(drawer)) {
        teardown();
      }
    }, { signal: signal });
  }

  function findLineItem(key) {
    if (!lastCart || !lastCart.items) return null;
    for (var i = 0; i < lastCart.items.length; i++) {
      if (lastCart.items[i].key === key) return lastCart.items[i];
    }
    return null;
  }

  // ---------- Upsell carousel ----------

  function bindUpsellCarousel(signal) {
    var upsellTrack = drawer.querySelector('[data-cart-upsell-track]');
    var upsellDots = drawer.querySelectorAll('.oddit-cart-drawer__upsell-dot');

    if (upsellTrack && upsellDots.length) {
      var setActiveDot = function (index) {
        Array.prototype.forEach.call(upsellDots, function (dot, i) {
          if (i === index) {
            dot.classList.add('oddit-cart-drawer__upsell-dot--active');
            dot.setAttribute('aria-selected', 'true');
          } else {
            dot.classList.remove('oddit-cart-drawer__upsell-dot--active');
            dot.setAttribute('aria-selected', 'false');
          }
        });
      };

      upsellTrack.addEventListener('scroll', function () {
        var cards = upsellTrack.querySelectorAll('.oddit-cart-drawer__upsell-card');
        if (!cards.length) return;
        var cardWidth = cards[0].getBoundingClientRect().width + 10;
        var index = Math.round(upsellTrack.scrollLeft / cardWidth);
        var clamped = Math.min(Math.max(0, index), upsellDots.length - 1);
        setActiveDot(clamped);
      }, { passive: true, signal: signal });

      Array.prototype.forEach.call(upsellDots, function (dot, i) {
        dot.addEventListener('click', function () {
          var cards = upsellTrack.querySelectorAll('.oddit-cart-drawer__upsell-card');
          if (!cards.length) return;
          var cardWidth = cards[0].getBoundingClientRect().width + 10;
          upsellTrack.scrollTo({ left: i * cardWidth, behavior: 'smooth' });
          setActiveDot(i);
        }, { signal: signal });
      });
    }

    if (upsellTrack) {
      var DRAG_THRESHOLD = 5;
      var isDown = false;
      var didDrag = false;
      var startX = 0;
      var startScroll = 0;

      upsellTrack.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;
        if (e.button !== 0) return;
        isDown = true;
        didDrag = false;
        startX = e.clientX;
        startScroll = upsellTrack.scrollLeft;
      }, { signal: signal });

      upsellTrack.addEventListener('pointermove', function (e) {
        if (!isDown) return;
        var dx = e.clientX - startX;
        if (!didDrag && Math.abs(dx) < DRAG_THRESHOLD) return;
        if (!didDrag) {
          didDrag = true;
          upsellTrack.classList.add('is-dragging');
          if (upsellTrack.setPointerCapture) {
            try { upsellTrack.setPointerCapture(e.pointerId); } catch (err) {}
          }
        }
        upsellTrack.scrollLeft = startScroll - dx;
        e.preventDefault();
      }, { signal: signal });

      var endDrag = function () {
        if (!isDown) return;
        isDown = false;
        if (!didDrag) return;
        upsellTrack.classList.remove('is-dragging');
        var swallow = function (ev) {
          ev.stopPropagation();
          ev.preventDefault();
          upsellTrack.removeEventListener('click', swallow, true);
        };
        upsellTrack.addEventListener('click', swallow, true);
        setTimeout(function () {
          upsellTrack.removeEventListener('click', swallow, true);
        }, 50);
      };

      upsellTrack.addEventListener('pointerup', endDrag, { signal: signal });
      upsellTrack.addEventListener('pointercancel', endDrag, { signal: signal });
      upsellTrack.addEventListener('lostpointercapture', endDrag, { signal: signal });

      upsellTrack.addEventListener('dragstart', function (e) {
        if (isDown) e.preventDefault();
      }, { signal: signal });
    }
  }

  // ---------- Init / teardown ----------

  function init() {
    drawer = document.querySelector(SECTION_SELECTOR + '[data-cart-drawer]') || document.querySelector(SECTION_SELECTOR);
    if (!drawer) return;

    // The drawer renders inside the header group, whose stacking context traps
    // it beneath the header. Portal it to <body> so its z-index applies against
    // the page root and it always layers above the header.
    if (drawer.parentNode !== document.body) {
      document.body.appendChild(drawer);
    }

    panel = drawer.querySelector('.oddit-cart-drawer__panel');
    overlay = drawer.querySelector('.oddit-cart-drawer__overlay');

    drawer.setAttribute('aria-hidden', 'true');

    bindEvents();

    // Initial hydration: the section is already server-rendered correctly, so
    // we only need the cart object to sync state and the header badge.
    getCart().then(function (cart) {
      applyCart(cart, null);
    }).catch(function () {});

    initialized = true;
  }

  function teardown() {
    if (abortController) {
      try { abortController.abort(); } catch (e) {}
      abortController = null;
    }
    if (isOpen) unlockBodyScroll();
    isOpen = false;
    initialized = false;
  }

  function unbindAndInit() {
    teardown();
    init();
  }

  // ---------- Public API ----------

  var publicAPI = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    // Re-render from the server. Accepts an optional cart object (for the
    // cart:updated event); markup always comes from the rendered section.
    renderCart: function (cart) {
      if (cart) {
        return fetchSectionHTML().then(function (html) {
          applyCart(cart, html);
          return cart;
        });
      }
      return refreshCart();
    },
    isOpen: function () { return isOpen; }
  };

  window.OdditCartDrawer = publicAPI;
  // Backwards-compat alias (in case any legacy code references the camelCase form).
  window.odditCartDrawer = publicAPI;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: section reloaded outside of our drawer scope
  document.addEventListener('shopify:section:load', function (event) {
    if (!initialized && event.target && event.target.querySelector && event.target.querySelector(SECTION_SELECTOR)) {
      unbindAndInit();
    }
  });
})();
